import type {
  Asset,
  AssetType,
  CaseRecord,
  CirculationRecord,
  DeliveryNote,
  DeliveryNoteItem,
  Invoice,
  InvoiceLineItem,
  Location,
  Peripheral,
  WarehouseData,
} from './types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Unbekannter Fehler');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export function fetchWarehouse(): Promise<WarehouseData> {
  return request<WarehouseData>('/api/warehouse', {
    headers: { 'content-type': 'application/json' },
  });
}

export function createAssetType(payload: Pick<AssetType, 'name' | 'description'>) {
  return request<AssetType>('/api/asset-types', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAssetType(id: string, payload: Pick<AssetType, 'name' | 'description'>) {
  return request<AssetType>(`/api/asset-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteAssetType(id: string) {
  return request<void>(`/api/asset-types/${id}`, { method: 'DELETE' });
}

export function createLocation(payload: Pick<Location, 'name' | 'description'>) {
  return request<Location>('/api/locations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateLocation(id: string, payload: Pick<Location, 'name' | 'description'>) {
  return request<Location>(`/api/locations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteLocation(id: string) {
  return request<void>(`/api/locations/${id}`, { method: 'DELETE' });
}

export function createCase(payload: Pick<CaseRecord, 'label' | 'locationId' | 'notes'>) {
  return request<CaseRecord>('/api/cases', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateCase(id: string, payload: Pick<CaseRecord, 'label' | 'locationId' | 'notes'>) {
  return request<CaseRecord>(`/api/cases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteCase(id: string) {
  return request<void>(`/api/cases/${id}`, { method: 'DELETE' });
}

export function createAsset(payload: Omit<Asset, 'id'>) {
  return request<Asset>('/api/assets', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAsset(id: string, payload: Omit<Asset, 'id'>) {
  return request<Asset>(`/api/assets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteAsset(id: string) {
  return request<void>(`/api/assets/${id}`, { method: 'DELETE' });
}

export function createPeripheral(payload: Omit<Peripheral, 'id'>) {
  return request<Peripheral>('/api/peripherals', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updatePeripheral(id: string, payload: Omit<Peripheral, 'id'>) {
  return request<Peripheral>(`/api/peripherals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deletePeripheral(id: string) {
  return request<void>(`/api/peripherals/${id}`, { method: 'DELETE' });
}

export function createCirculation(payload: Omit<CirculationRecord, 'id'>) {
  return request<CirculationRecord>('/api/circulation', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function deleteCirculation(id: string) {
  return request<void>(`/api/circulation/${id}`, { method: 'DELETE' });
}

export function createInvoice(payload: {
  number: string;
  issueDate: string;
  dueDate?: string;
  customerName: string;
  customerAddress?: string;
  currency: string;
  lineItems: Array<Omit<InvoiceLineItem, 'id'>>;
}) {
  return request<Invoice>('/api/invoices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function deleteInvoice(id: string) {
  return request<void>(`/api/invoices/${id}`, { method: 'DELETE' });
}

export function createDeliveryNote(payload: {
  number: string;
  issueDate: string;
  recipientName: string;
  recipientAddress?: string;
  items: Array<Omit<DeliveryNoteItem, 'id'>>;
}) {
  return request<DeliveryNote>('/api/delivery-notes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function deleteDeliveryNote(id: string) {
  return request<void>(`/api/delivery-notes/${id}`, { method: 'DELETE' });
}

export function invoiceDownloadUrl(id: string, type: 'xml' | 'pdf'): string {
  return `${API_BASE}/api/invoices/${id}/${type === 'xml' ? 'ubl' : 'pdf'}`;
}

export function deliveryNoteDownloadUrl(id: string, type: 'xml' | 'pdf'): string {
  return `${API_BASE}/api/delivery-notes/${id}/${type}`;
}

export function computeAssetValue(assets: Asset[]): number {
  return assets.reduce((sum, asset) => sum + (asset.purchasePrice ?? 0), 0);
}

export function formatCurrency(value: number, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(value);
}
